<script>
import Button from '@/components/Button';
import { mapState } from 'vuex';
import SalePoolDetails from '@/components/Launchpad/SalePoolDetails';
import SaleHeader from '@/components/Launchpad/SaleHeader';
import SalePreviewHeader from '@/components/Launchpad/SalePreviewHeader';
import SaleProgressBar from '@/components/Launchpad/SaleProgressBar';
import SaleStageData from '@/components/Launchpad/SaleStageData';
import SaleStageButtons from '@/components/Launchpad/SaleStageButtons';
import AllocationProgressBar from '@/components/Launchpad/AllocationProgressBar';
import { token, products } from '@/constants/launchpad';
import { getSignature } from '@/helpers/api/launchpad';
import { getDateByTimestamp } from '../../utils/dateByBlock';

export default {
  name: 'Tokensale',
  components: {
    Button,
    SalePoolDetails,
    SaleHeader,
    SaleProgressBar,
    SaleStageData,
    SaleStageButtons,
    AllocationProgressBar,
    SalePreviewHeader,
  },

  data() {
    return {
      currentStage: 0,
      info: {
        tokenBalance: '0',
        nextStageDate: null,
        stakeAllocationAmount: 0,
        tokenPrice: 0,
        name: '',
        tokenAmount: '-',
        receivedPayments: '-',
        tokenName: '-',
        paymentTokenName: '-',
        percent: 0,
        isClaimed: false,
        tokensBought: 0,
        bought: 0,
        leftToBuy: '0',
        isMaxParticipants: true,
        stake: 0,
        allocationPercent: 0,
        allocationTotal: 1,
        isMaxStake: false,
        isWhiteListed: true,
        saleStartTimestamp: 0,
      },
      timer: null,
      stakeContract: '',
      saleContract: '',
    };
  },

  computed: {
    ...mapState({
      ethAddress: (state) => state.User.ethAddress,
      chainId: (state) => state.User.chainId,
    }),
    isAllowedChain() {
      return [1, 4, 5, 56, 97, 43113, 43114].includes(this.chainId);
    },
    saleId() {
      return this.productParams.id;
    },
    saleCampaign() {
      return this.$route.params.pid;
    },
    saleChainId() {
      return this.productParams.chainId;
    },
    isBinance() {
      return [56, 97].includes(this.chainId);
    },
    lockerId() {
      return Number(this.productParams.lockerId);
    },
    productParams() {
      return products[this.saleCampaign] || {};
    },
  },

  async mounted() {
    await this.init();
    this.$contracts
      .getContract(this.saleContract)
      .events.SaleAllocAdded({ filter: { saleid: this.saleId } }, () => {
        this.init();
      });
    this.$contracts
      .getContract(this.stakeContract)
      .events.Stake({ filter: { staker: this.ethAddress } }, () => {
        this.init();
      });
  },

  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },

  methods: {
    updateComponent() {
      this.init();
    },
    async setupTimer() {
      this.timer = setInterval(async () => {
        const updatedStage = await this.getCurrentStage();
        if (this.currentStage !== updatedStage) {
          clearInterval(this.timer);
          this.init();
        }
      }, 15000);
    },
    async getSaleBalanceRest() {
      try {
        const amount = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSaleBalanceRest(this.saleId, this.ethAddress)
          .call();
        return {
          balanceRest: this.$contracts.fromWei(amount[0]).toString(),
          balance: this.$contracts.fromWei(amount[1]).toString(),
        };
      } catch (e) {
        return false;
      }
    },
    async getSaleMaxStake() {
      try {
        const arr = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSaleRecord2(this.saleId)
          .call();
        return this.$contracts.fromWei(arr[1]).toNumber();
      } catch (e) {
        return 0;
      }
    },
    async isInWhiteList() {
      try {
        const { message, signature } = await getSignature(this.ethAddress);
        const listed = await this.$contracts
          .getContract('launchpadWhiteList')
          .methods.enabledForCampaign(this.ethAddress, this.saleId, message, signature)
          .call();
        return listed;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    async getCurrentStage() {
      try {
        const currentStage = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSalestage(this.saleId)
          .call();
        return this.isBinance ? Number(currentStage) : 4;
      } catch (e) {
        return 0;
      }
    },
    async getStageTimestamp() {
      const saleTimestamps = await this.getSaleRecord();
      return this.currentStage === 2 ? saleTimestamps[2] : saleTimestamps[4];
    },
    async getTokenBalance() {
      return this.$contracts
        .getContract(this.info.paymentToken)
        .methods.balanceOf(this.ethAddress)
        .call();
    },
    async getSaleRecord() {
      try {
        const saleRecord = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSaleRecord(this.saleId)
          .call();
        return saleRecord[5];
      } catch (e) {
        return 0;
      }
    },
    async init() {
      this.stakeContract = this.productParams?.stakeToken === 'shake' ? 'launchpadStakingShake' : 'launchpadStaking';
      this.saleContract = ['sidus', 'senate', 'penguinkarts'].includes(this.saleCampaign) ? 'launchpadSaleOld' : 'launchpadSale';
      this.currentStage = await this.getCurrentStage();
      const saleInfo = await this.getSaleInfo();
      const balance = (await this.getSaleBalanceRest()) || { balance: '0', balanceRest: '0' };
      const viewSale = await this.viewSaleInfo();
      const stageTimestamp = await this.getStageTimestamp();
      const participants = await this.getSaleParticipants();
      const saleTimestamps = await this.getSaleRecord();
      const denominator = this.isBinance ? 10000 : 1000;

      this.info = {
        nextStageDate: await getDateByTimestamp(stageTimestamp),
        balance,
        stakeAllocationAmount: this.$BN(balance.balanceRest).plus(balance.balance),
        currentAllocation: this.$contracts.fromWei(viewSale.currAllocation).toNumber(),
        tokenPrice: saleInfo.tokenPrice / denominator,
        name: saleInfo.name,
        tokenAmount: this.$contracts.fromWei(saleInfo.tokenAmount).toNumber(),
        receivedPayments: this.$contracts.fromWei(saleInfo.receivedPayments).toNumber(),
        token: token[saleInfo.token].name,
        paymentToken: token[saleInfo.paymentToken].name,
        tokenName: token[saleInfo.token].fullName,
        paymentTokenName: token[saleInfo.paymentToken].fullName,
        isClaimed: viewSale.claimed,
        tokensBought: this.$BN(balance.balance)
          .div(saleInfo.tokenPrice)
          .times(denominator),
        bought: this.$BN(balance.balance),
        leftToBuy: balance.balanceRest,
        isMaxParticipants: await this.checkSaleParticipants(),
        participants: participants.totalParticipants,
        maxParticipants: participants.maxParticipant,
        stake: await this.getCurrentStake(),
        minStake: await this.getSaleMinStake(),
        maximumStake: await this.getSaleMaxStake(),
        allocationTotal: this.$contracts.fromWei(saleInfo.stakeAllocationAmount).toNumber(),
        isMaxStake: await this.isMaxStake(),
        isWhiteListed: await this.isInWhiteList(),
        saleStartTimestamp: Number(saleTimestamps[0]) || 0,
        saleStartDate: await getDateByTimestamp(saleTimestamps[0]),
      };

      this.info.percent = (
        this.info.receivedPayments / (this.info.tokenAmount * this.info.tokenPrice)
      ) * 100;
      this.info.allocationPercent = this.info.allocationTotal
        ? (this.info.currentAllocation / this.info.allocationTotal) * 100
        : 0;
      this.info.tokenBalance = this.$contracts.fromWei(await this.getTokenBalance()).toString();
      this.setupTimer();
    },
    async isMaxStake() {
      const saleMaxStake = await this.getSaleMaxStake();
      const viewSale = await this.viewSaleInfo();
      const currentAllocation = this.$contracts.fromWei(viewSale.currAllocation).toNumber();
      return currentAllocation >= saleMaxStake;
    },
    async getCurrentStake() {
      try {
        const amount = await this.$contracts
          .getContract(this.stakeContract)
          .methods.userStakes(this.ethAddress)
          .call();
        return this.$contracts.fromWei(amount.amount).toNumber();
      } catch (e) {
        return 0;
      }
    },
    async getSaleMinStake() {
      try {
        const arr = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSaleRecord2(this.saleId)
          .call();
        return this.$contracts.fromWei(arr[0]).toNumber();
      } catch (e) {
        return 0;
      }
    },
    async getSaleInfo() {
      try {
        return await this.$contracts
          .getContract(this.saleContract)
          .methods.sales(this.saleId)
          .call();
      } catch (e) {
        return false;
      }
    },
    async getSaleParticipants() {
      try {
        const saleParticipants = await this.$contracts
          .getContract(this.saleContract)
          .methods.getSaleParticipants(this.saleId)
          .call();
        return {
          totalParticipants: Number(saleParticipants.totalParticipants),
          maxParticipant: Number(saleParticipants.maxParticipant),
        };
      } catch (e) {
        return false;
      }
    },
    async checkSaleParticipants() {
      const participants = await this.getSaleParticipants();
      return participants.totalParticipants >= participants.maxParticipant;
    },
    async viewSaleInfo() {
      try {
        return await this.$contracts
          .getContract(this.saleContract)
          .methods.viewSale(this.saleId, this.ethAddress)
          .call();
      } catch (e) {
        return {};
      }
    },
  },
};
</script>

<template>
  <div class="main-wrap launchpad main-wrap--narrow">
      <div v-if="!isAllowedChain">
        <v-row justify="center">
          <h1>{{ $t('upcoming.ethereum-only') }}</h1>
        </v-row>
      </div>
      <div v-else-if="Object.keys(productParams).length === 0">
        <v-row justify="center">
          <h1>{{ $t('upcoming.no-products') }}</h1>
        </v-row>
      </div>
      <v-container class="mt-10 mb-5" v-else>
        <div class="upcoming-card main-block mb-9" v-if="chainId === saleChainId">
          <SaleHeader
            :currentStage="currentStage"
            :nextStageDate="info.nextStageDate"
            :saleStartDate="info.saleStartDate"
            :name="info.name"
            :product="productParams"
            :saleStartTimestamp="info.saleStartTimestamp"
          />
          <div class="mb-5">
            <p class="upcoming-card__disclaimer">
              {{ productParams.description }}
            </p>
          </div>
          <AllocationProgressBar :currentStage="currentStage" :info="info" />
          <SaleProgressBar
            :currentStage="currentStage"
            :info="info"
            :product="productParams"
          />
          <SaleStageData
            :currentStage="currentStage"
            :info="info"
            :isBinance="isBinance"
            :hasClaim="productParams.hasClaim"
            :stakeToken="productParams.stakeToken"
          />
          <SaleStageButtons
            :currentStage="currentStage"
            :info="info"
            :isBinance="isBinance"
            :hasClaim="productParams.hasClaim"
            :lockerId="lockerId"
            :saleId="String(saleId)"
            @update="updateComponent"
          />
        </div>
        <div class="upcoming-card main-block mb-9" v-else>
          <SalePreviewHeader :name="productParams.name" :product="productParams" />
          <div class="mb-5">
            <p class="upcoming-card__disclaimer">
              {{ productParams.description }}
            </p>
          </div>
          <div class="mt-10 text-center">
            <h1 class="upcoming-card__disclaimer">
              {{ $t('launchpad.switch') }}
            </h1>
          </div>
        </div>
        <div class="text-center mb-5">
          <h4 class="launchpad__subline">{{ $t('upcoming.details') }}</h4>
        </div>
        <SalePoolDetails :product="productParams" />
        <v-row>
          <v-col cols="12" class="my-10 text-center">
            <Button to="/staking/">{{ $t('launchpad.staking') }}</Button>
          </v-col>
        </v-row>
      </v-container>
    </div>
</template>

<style lang="scss">
.launchpad {
  &__subline {
    font-weight: 700;
    font-size: 3.4rem;

    &--primary {
      color: $main-pink;
    }
  }

  &__link {
    font-size: 1.8rem;
    margin-top: 5rem;
  }

  &-title {
    &--zero-stage {
      width: 100%;
      height: 4rem;
      margin: 2rem auto;
      background: url('../../assets/img/ethereum/launchpad/wait-next-stage.png') no-repeat center
        center / contain;

      &-bsc {
        background: url('../../assets/img/binance/launchpad/wait-next-stage.png') no-repeat center
          center / contain;
        height: 9rem;
      }
    }
  }
}

.upcoming-info {
  .col {
    font-size: 1.6rem;
    font-weight: 600;

    &:first-child {
      text-transform: uppercase;
      color: $grey-font-color;

      body.binance & {
        color: $b-grey-font-color;
      }
    }

    &:last-child {
      text-align: right;
    }
  }
}

.upcoming-card {
  height: 100%;
  padding: 2.8rem 3.2rem 2.9rem 3.2rem;

  &__disclaimer {
    font-size: 1.75rem;
    line-height: 1.5;
  }

  .v-progress-linear {
    border-radius: 50px;
    box-shadow: 1px 1px 2px rgba(255, 255, 255, 0.3), 0px -3px 3px rgba(255, 161, 145, 0.5),
      inset -1px 1px 2px rgba(255, 161, 145, 0.2), inset 1px -1px 2px rgba(255, 161, 145, 0.2),
      inset -1px -1px 2px rgba(255, 255, 255, 0.9), inset 1px 1px 3px rgba(255, 161, 145, 0.9);

    span {
      font-size: 2.2em;
      font-weight: 600;
      color: $main-font-color;

      body.binance & {
        color: $b-dark-font-color;
      }
    }

    &.afterhalf span {
      color: #fff;

      body.binance & {
        color: $b-dark-font-color;
      }
    }
  }

  .progress__title {
    font-size: 2.2rem;

    @media (max-width: $breakpoint-sm) {
      font-size: 1.6rem;
    }
  }

  .progress__subtitle {
    font-size: 2.2em;
    font-weight: 600;

    @media (max-width: $breakpoint-sm) {
      font-size: 1.6rem;
    }
  }

  &__title {
    font-size: 3.4rem;

    @media (max-width: $breakpoint-sm) {
      font-size: 2.5rem;
    }
  }

  &__step {
    position: relative;
    padding: 1rem 1.6rem 1rem 3.2rem;
    font-size: 1.4rem;
    font-weight: 600;
    color: #ff9d9d;
    background: #ffffff;
    box-shadow: 10px 10px 20px rgba(222, 222, 222, 0.25), 2px 2px 20px rgba(212, 202, 205, 0.2),
      -10px -10px 15px #ffffff, 10px 10px 20px rgba(181, 155, 155, 0.25), inset 2px 2px 6px #ffffff,
      inset -1px -1px 2px rgba(181, 155, 155, 0.2);
    border-radius: 49px;

    &::before {
      display: block;
      content: '';
      position: absolute;
      width: 0.8rem;
      height: 0.8rem;
      left: 1.5rem;
      top: 1.6rem;
      background: #ff9d9d;
      border-radius: 50%;
    }

    &--green {
      color: $main-green;

      &::before {
        background: $main-green;
      }
    }

    &--red {
      color: $main-pink;

      &::before {
        background: $main-pink;
      }
    }
  }

  &__start {
    font-size: 1.6rem;
    font-weight: bold;

    span {
      font-weight: 600;
    }
  }

  .btn {
    padding: 2rem 9rem;
    font-size: 2rem;

    &--pink {
      color: #ffffff;
      background: #fb3a90;
      box-shadow: 0px 15px 70px -17px #fb3a90;
    }
  }

  &__icon {
    background: $btn-bg;
    box-shadow: $btn-shadow;
    padding: 1.5rem;
    border-radius: 50%;
    margin-bottom: 1rem;

    @media (max-width: $breakpoint-sm) {
      padding: 1rem;
    }

    img {
      width: 10.5rem;
      height: 10.5rem;
      border-radius: 50%;

      @media (max-width: $breakpoint-sm) {
        width: 6rem;
        height: 6rem;
      }
    }
  }

  .footer__social a {
    @media (max-width: $breakpoint-sm) {
      width: 3rem;
      height: 3rem;
    }
  }

  p {
    margin-bottom: 1.5rem;
  }

  .label {
    font-size: 2.4em;
    font-weight: 600;
  }

  .data {
    font-size: 2.8rem;
    font-weight: 600;
  }

  .registered {
    font-size: 2.2rem;
    font-weight: bold;
  }
}
</style>
